import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button, Input } from '../components/ui';
import { Save, Store, User, Mail, Phone, MapPin, Globe } from 'lucide-react';

const Profile: React.FC = () => {
  const { t } = useTranslation('profile');
  const { seller, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [shopFormData, setShopFormData] = useState<{
    name: string;
    type: 'individual' | 'company';
    logo: string;
  }>({
    name: seller?.name || '',
    type: (seller?.type === 'company' ? 'company' : 'individual') || 'individual',
    logo: seller?.logo || '',
  });
  const [userFormData, setUserFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    nationality: user?.nationality || '',
    currentLocation: user?.currentLocation || '',
    profileImageUrl: user?.profileImageUrl || '',
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

  useEffect(() => {
    if (seller) {
      setShopFormData({
        name: seller.name || '',
        type: seller.type === 'company' ? 'company' : 'individual',
        logo: seller.logo || '',
      });
    }
  }, [seller]);

  useEffect(() => {
    if (user) {
      setUserFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        nationality: user.nationality || '',
        currentLocation: user.currentLocation || '',
        profileImageUrl: user.profileImageUrl || '',
      });
    }
  }, [user]);

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await api.post('/upload-single-file', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.data?.fileUrl) {
        const logoUrl = response.data.data.fileUrl;
        console.log('Logo uploaded successfully, URL:', logoUrl);
        setShopFormData(prev => ({
          ...prev,
          logo: logoUrl
        }));
        // Show success message
        alert(t('messages.logoUploaded'));
      } else {
        console.error('No fileUrl in response:', response.data);
        alert(t('messages.logoUploadFailed'));
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert(t('messages.logoUploadError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleProfileImageUpload = async (file: File) => {
    setUploadingProfileImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('profilePicture', file);

      const response = await api.put('/user/profile', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.data) {
        const updatedUser = response.data.data;
        if (updatedUser.profileImageUrl) {
          setUserFormData(prev => ({
            ...prev,
            profileImageUrl: updatedUser.profileImageUrl
          }));
        }
        alert(t('messages.profileImageUpdated'));
        window.location.reload();
      } else {
        alert(t('messages.profileImageUpdatedUnexpected'));
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to upload profile image:', error);
      alert(t('messages.profileImageUploadError'));
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleShopSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log('Submitting shop profile with data:', shopFormData);
      console.log('Logo being sent:', shopFormData.logo);
      const response = await api.put('/seller/profile', shopFormData);
      console.log('Shop profile update response:', response.data);
      alert(t('messages.shopProfileUpdated'));
      window.location.reload();
    } catch (error) {
      console.error('Failed to update shop profile:', error);
      alert(t('messages.shopProfileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      await api.patch('/user/profile', {
        firstName: userFormData.firstName,
        lastName: userFormData.lastName,
        phoneNumber: userFormData.phoneNumber,
        nationality: userFormData.nationality,
        currentLocation: userFormData.currentLocation,
      });
      alert(t('messages.userProfileUpdated'));
      window.location.reload();
    } catch (error) {
      console.error('Failed to update user profile:', error);
      alert(t('messages.userProfileUpdateError'));
    } finally {
      setSavingUser(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600 mt-2">{t('subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shop Profile Form */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Store className="text-custom-primary" size={24} />
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">{t('sections.shopProfile')}</h2>
          </div>
          <form onSubmit={handleShopSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fields.shopName')}
              </label>
              <Input
                value={shopFormData.name}
                onChange={(e) => setShopFormData({ ...shopFormData, name: e.target.value })}
                placeholder={t('placeholders.shopName')}
                icon={Store}
                iconPosition="left"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fields.shopType', { defaultValue: 'Shop Type' })}
              </label>
              <select
                value={shopFormData.type}
                onChange={(e) => setShopFormData({ ...shopFormData, type: e.target.value as 'individual' | 'company' })}
                className="input-field"
              >
                <option value="individual">{t('shopType.individual')}</option>
                <option value="company">{t('shopType.enterprise')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fields.shopLogo')}
              </label>
              {shopFormData.logo && (
                <img
                  src={shopFormData.logo}
                  alt="Shop logo"
                  className="w-24 h-24 object-cover rounded-lg mb-4 border border-gray-200"
                />
              )}
              {!shopFormData.logo && (
                <div className="w-24 h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
                  <Store size={32} className="text-gray-400" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoUpload(file);
                }}
                className="input-field"
                disabled={uploadingLogo}
              />
              {uploadingLogo && (
                <p className="text-sm text-gray-500 mt-2">{t('messages.uploading')}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              icon={Save}
              iconPosition="left"
              loading={saving || uploadingLogo}
              className="w-full"
            >
              {t('buttons.saveShopChanges')}
            </Button>
          </form>
        </div>

        {/* User Profile Form */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <User className="text-custom-primary" size={24} />
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">{t('sections.personalProfile')}</h2>
          </div>
          <form onSubmit={handleUserSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fields.profileImage')}
              </label>
              {userFormData.profileImageUrl && (
                <img
                  src={userFormData.profileImageUrl}
                  alt="Profile"
                  className="w-24 h-24 object-cover rounded-full mb-4 border-2 border-gray-200"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleProfileImageUpload(file);
                }}
                className="input-field"
                disabled={uploadingProfileImage}
              />
              {uploadingProfileImage && (
                <p className="text-sm text-gray-500 mt-2">{t('messages.uploading')}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('fields.firstName')}
                value={userFormData.firstName}
                onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                placeholder={t('placeholders.firstName')}
                icon={User}
                iconPosition="left"
                required
              />
              <Input
                label={t('fields.lastName')}
                value={userFormData.lastName}
                onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                placeholder={t('placeholders.lastName')}
                icon={User}
                iconPosition="left"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fields.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="input-field pl-12 bg-gray-100 cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('messages.emailCannotBeChanged')}</p>
            </div>

            <Input
              label={t('fields.phoneNumber')}
              type="tel"
              value={userFormData.phoneNumber}
              onChange={(e) => setUserFormData({ ...userFormData, phoneNumber: e.target.value })}
              placeholder={t('placeholders.phoneNumber')}
              icon={Phone}
              iconPosition="left"
            />

            <Input
              label={t('fields.nationality')}
              value={userFormData.nationality}
              onChange={(e) => setUserFormData({ ...userFormData, nationality: e.target.value })}
              placeholder={t('placeholders.nationality')}
              icon={Globe}
              iconPosition="left"
            />

            <Input
              label={t('fields.currentLocation')}
              value={userFormData.currentLocation}
              onChange={(e) => setUserFormData({ ...userFormData, currentLocation: e.target.value })}
              placeholder={t('placeholders.currentLocation')}
              icon={MapPin}
              iconPosition="left"
            />

            <Button
              type="submit"
              variant="primary"
              icon={Save}
              iconPosition="left"
              loading={savingUser}
              className="w-full"
            >
              {t('buttons.savePersonalChanges')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;

